import { Bot, ChevronRight } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useWorkspaceDirectory } from '@/lib/assistants/directory';
import { httpWorkspaceDirectoryApi } from '@/lib/assistants/directory-client';
import {
  type ChildConversation,
  childConversation,
  originLabel,
  taskChipLabel,
} from '@/lib/conversations/child-conversation';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';

/**
 * The conversation as a child of another, from its thread record's placement, else the
 * assistant directory's; null when it is not one.
 */
export function useChildConversation(conversationId: string | null): ChildConversation | null {
  const store = useOptionalDesktopShellStoreApi();
  const directory = useWorkspaceDirectory(
    useShallow((state) => ({
      placements: state.placements,
      projects: state.projects,
    })),
  );
  const threads = store?.getState().syncedThreads ?? [];
  const recorded =
    conversationId === null
      ? undefined
      : threads.find((thread) => thread.id === conversationId)?.placement;
  const placement =
    recorded ?? (conversationId === null ? undefined : directory.placements[conversationId]);
  const projectId = placement?.workflowRun == null ? null : placement.projectId;
  const taskOrder = useProjectTaskOrder(projectId);
  if (conversationId === null) return null;
  return childConversation({
    taskOrder,
    conversationId,
    ...directory,
    placements:
      recorded === undefined
        ? directory.placements
        : { ...directory.placements, [conversationId]: recorded },
    titleOf: (id) => threads.find((thread) => thread.id === id)?.title ?? null,
  });
}

/** The project's tasks, oldest first (its Tasks list newest first), once read. */
function useProjectTaskOrder(projectId: string | null): readonly string[] | undefined {
  const [order, setOrder] = useState<{ projectId: string; ids: string[] } | null>(null);
  useEffect(() => {
    if (projectId === null) return;
    let live = true;
    void httpWorkspaceDirectoryApi.assistantTasks(projectId).then(
      (tasks) => {
        if (live) setOrder({ projectId, ids: tasks.map((task) => task.conversationId).reverse() });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [projectId]);
  return order !== null && order.projectId === projectId ? order.ids : undefined;
}

/** "■ Crowdex Mobile ›" before the title: back to the parent (design D49). */
export function ChildConversationBreadcrumb({ conversationId }: { conversationId: string }) {
  const child = useChildConversation(conversationId);
  const opener = useConversationOpener();
  if (child === null) return null;
  return <ParentLink child={child} onOpen={() => void opener?.openConversation(child.parentId)} />;
}

export function ParentLink({ child, onOpen }: { child: ChildConversation; onOpen?: () => void }) {
  return (
    <button
      aria-label={`Back to ${child.parentTitle}`}
      className="inline-flex min-w-0 shrink items-center gap-1.5 text-muted-foreground text-sm transition-colors hover:text-foreground"
      data-child-breadcrumb
      onClick={onOpen}
      type="button"
    >
      {child.parentColor !== null && (
        <span
          aria-hidden
          className="size-2.5 shrink-0 rounded-[3px]"
          style={{ backgroundColor: child.parentColor }}
        />
      )}
      <span className="truncate">{child.parentTitle}</span>
      <ChevronRight aria-hidden className="size-3.5 shrink-0" />
    </button>
  );
}

/** "Task 2 of 5 · Careful · Develop" beside the title of a workflow's task. */
export function WorkflowTaskChip({ conversationId }: { conversationId: string }) {
  const child = useChildConversation(conversationId);
  if (child?.task == null) return null;
  return (
    <span
      className="type-meta inline-flex h-6 shrink-0 items-center rounded-control bg-control px-2.5 text-foreground/85"
      data-workflow-task-chip
    >
      {taskChipLabel(child.task)}
    </span>
  );
}

/** The task the child conversation was given, as its first message (design D49). */
export function OriginCard({
  child,
  children,
}: {
  child: Pick<ChildConversation, 'parentTitle' | 'task'>;
  children: ReactNode;
}) {
  return (
    <section
      className="w-full max-w-[min(100%,40rem)] rounded-card bg-bubble px-3.5 py-3"
      data-origin-card
    >
      <p className="type-meta flex items-center gap-1.5 text-muted-foreground">
        <Bot aria-hidden className="size-3.5" />
        {originLabel({ parentId: '', parentColor: null, ...child })}
      </p>
      <div className="mt-1 text-foreground text-sm leading-6">{children}</div>
    </section>
  );
}
